import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi, getApiErrorMessage } from '../services/adminApi'
import { useAdminAuth } from '../context/AdminAuthContext'
import { Alert, Button, Card, PageHeader, Spinner, StatCard } from '../components/ui'
import { formatBytes } from '../utils/format'

function formatUptime(seconds) {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${h}h ${m}m`
}

export default function Dashboard() {
  const { admin, isMaster, isRevenda } = useAdminAuth()
  const [stats, setStats] = useState(null)
  const [monitor, setMonitor] = useState(null)
  const [tenantCount, setTenantCount] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        if (isRevenda) {
          const { data } = await adminApi.listRevendaTenants()
          if (cancelled) return
          const tenants = data.data || []
          const active = tenants.filter((t) => t.status === 'active').length
          setTenantCount(tenants.length)
          setStats({ totalTenants: tenants.length, activeTenants: active })
        } else {
          const tasks = [adminApi.stats(), adminApi.listTenants()]
          if (isMaster) tasks.push(adminApi.monitoringCurrent().catch(() => null))
          const results = await Promise.all(tasks)
          if (cancelled) return
          setStats(results[0].data.data)
          const tenants = results[1].data.data || []
          setTenantCount(tenants.length)
          if (isMaster && results[2]) {
            setMonitor(results[2].data.data)
          }
        }
      } catch (err) {
        if (!cancelled) setError(getApiErrorMessage(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [isMaster, isRevenda])

  if (loading) return <Spinner />

  const displayName = admin?.name || admin?.email?.split('@')[0] || 'Administrador'
  const quickActions = [
    { to: '/tenants', icon: 'fa-building', title: isRevenda ? 'Minhas bases' : 'Gerenciar clientes', text: 'Cadastros, limites e acessos', tone: 'blue' },
    ...(isMaster ? [
      { to: '/monitoring', icon: 'fa-heart-pulse', title: 'Monitoramento', text: 'Saúde dos serviços em tempo real', tone: 'cyan' },
      { to: '/prices', icon: 'fa-tags', title: 'Tabelas de preço', text: 'Faixas e regras comerciais', tone: 'violet' },
      { to: '/billing', icon: 'fa-file-invoice-dollar', title: 'Faturamento', text: 'Consolidados por revenda', tone: 'green' },
    ] : []),
  ]

  return (
    <div className="mp-dashboard space-y-6">
      <PageHeader
        eyebrow="Visão geral"
        title={`Olá, ${displayName}`}
        description="Acompanhe os principais indicadores e acesse rapidamente as rotinas administrativas."
        icon="fa-table-cells-large"
        actions={(
          <span className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-xs font-semibold text-white/90 backdrop-blur">
            <i className="fas fa-calendar-day text-cyan-200" aria-hidden="true" />
            {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' })}
          </span>
        )}
      />

      {error && <Alert type="error">{error}</Alert>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={isRevenda ? 'Minhas bases' : 'Total de clientes'}
          value={isRevenda ? (tenantCount ?? '—') : (stats?.totalTenants ?? tenantCount ?? '—')}
          icon="fa-building"
        />
        <StatCard
          label={isRevenda ? 'Bases ativas' : 'Clientes ativos'}
          value={isRevenda ? (stats?.activeTenants ?? '—') : (stats?.activeTenants ?? tenantCount ?? '—')}
          icon="fa-circle-check"
          tone="green"
        />
        {isMaster && (
          <>
            <StatCard label="Health score" value={`${stats?.healthScore ?? '—'}%`} icon="fa-heart-pulse" tone="cyan" />
            <StatCard
              label="Uptime do servidor"
              value={stats?.system?.uptime != null ? formatUptime(stats.system.uptime) : '—'}
              sub={
                stats?.system?.memoryUsage != null
                  ? `RAM: ${stats.system.memoryUsage.toFixed(1)} MB`
                  : null
              }
              icon="fa-server"
              tone="amber"
            />
          </>
        )}
      </div>

      {isMaster && monitor && (
        <Card title="Monitoramento em tempo real" description="Estado consolidado dos serviços e bases." lift>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Tenants"
              value={monitor.db?.tenantsCount ?? '—'}
              sub={`DBs: ${monitor.db?.details?.length ?? 0}`}
              icon="fa-database"
            />
            <StatCard
              label="Tamanho total DB"
              value={formatBytes(monitor.db?.totalSize)}
              icon="fa-hard-drive"
              tone="cyan"
            />
            <StatCard
              label="Fotos (7 dias)"
              value={monitor.photos?.totalLastWeek ?? 0}
              icon="fa-images"
              tone="green"
            />
            <StatCard
              label="E-mail"
              value={monitor.services?.email?.status === 'ok' ? 'OK' : 'Verificar'}
              sub={monitor.services?.email?.message}
              icon="fa-envelope"
              tone="amber"
            />
          </div>
          <div className="mt-4">
            <Link to="/monitoring">
              <Button variant="outline">Ver monitoramento completo</Button>
            </Link>
          </div>
        </Card>
      )}

      <Card title="Acesso rápido" description="As rotinas que você mais usa, sempre à mão.">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {quickActions.map((action) => (
            <Link key={action.to} to={action.to} className="mp-quick-action group flex items-center gap-3 rounded-2xl border p-3.5 transition">
              <span className={`mp-quick-icon mp-quick-icon-${action.tone} grid h-11 w-11 shrink-0 place-items-center rounded-xl text-sm`}>
                <i className={`fas ${action.icon}`} aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-brand-deep">{action.title}</span>
                <span className="mt-0.5 block truncate text-xs text-slate-500">{action.text}</span>
              </span>
              <i className="fas fa-arrow-right text-xs text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-brand-primary" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </Card>
    </div>
  )
}
